import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { useFileDownloadUrl } from '../files/hooks'
import type { FileAttachment } from '../files/types'

type Props = {
  contractId: number
  fileIds: number[]
  startIndex?: number
  filesMeta?: FileAttachment[]
  onClose: () => void
}

/**
 * Pełnoekranowy lightbox do przeglądania obrazków - wspólny dla pojedynczego
 * pola FILE i wielokrotnej GALLERY. Strzałki ‹›/Esc, klik tła zamyka.
 */
export function FileLightbox({ contractId, fileIds, startIndex = 0, filesMeta = [], onClose }: Props) {
  const [idx, setIdx] = useState(startIndex)
  const fid = fileIds[idx]
  const urlQ = useFileDownloadUrl(contractId, fid)
  const meta = filesMeta.find(f => f.id === fid)

  const prev = () => setIdx(i => (i - 1 + fileIds.length) % fileIds.length)
  const next = () => setIdx(i => (i + 1) % fileIds.length)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft' && fileIds.length > 1) prev()
      if (e.key === 'ArrowRight' && fileIds.length > 1) next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileIds.length])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose() }}
        className="absolute top-4 right-4 size-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        title="Zamknij (Esc)"
      >
        <X size={18} />
      </button>
      {fileIds.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev() }}
            className="absolute left-4 top-1/2 -translate-y-1/2 size-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-2xl"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next() }}
            className="absolute right-4 top-1/2 -translate-y-1/2 size-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-2xl"
          >
            ›
          </button>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-2">
        {urlQ.data && (
          <img
            src={urlQ.data.url}
            alt={meta?.originalFilename ?? `plik ${fid}`}
            className="max-w-full max-h-[80vh] object-contain rounded shadow-2xl"
          />
        )}
        {meta && (
          <div className="text-xs text-white/80 bg-black/40 rounded px-3 py-1">
            {meta.originalFilename}
            {fileIds.length > 1 && <> · {idx + 1} / {fileIds.length}</>}
          </div>
        )}
      </div>
    </div>
  )
}
